import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { getAllFilterKeys } from 'components/Filters/utils';
import { getMatchingFilters } from 'components/HotelCard/PartialFiltersMatch/getMatchingFilters';
import { UnmatchedFilters } from 'components/Map/UnmatchedFilters';
import { OfferPrice } from 'components/Offer/Shared/OfferPrice';
import { useFiltersForm } from 'hooks/useFiltersForm';
import placeHolderImage from 'images/placeholder-hotel-image.svg';
import { getCurrencyCode } from 'modules/meta/selectors';
import { getAnchorComplete, getAnchorHotelId, getHotel, getHotelTopOffer, getIsSearchPending, getOfferEntity, getShowTotalPrices, getSplitBookingDetails, getSplitBookingPriceSummary, State } from 'modules/sapiSearch/selectors';
import { Hotel, SearchOffer } from 'modules/sapiSearch/slice';
import { getAnchorPriceTotal } from 'modules/sapiSearch/utils';
import { getListOfValues } from 'modules/search/selectors';
import { pathOr, prop } from 'ramda';
import Settings from 'Settings';
import { imageProvider as imageProviderInstance } from 'utils/imageProvider';
import { getAnchorPrice } from 'utils/offers';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { NoStyleButton } from '@daedalus/atlas/helpers/NoStyleButton';
import { Icon } from '@daedalus/atlas/Icon';
import { PriceSize } from '@daedalus/atlas/Price';
import { PriceComposition } from '@daedalus/atlas/PriceComposition';
import { getEnabledCurrencies } from '@daedalus/core/src/localization/business/currency';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { hasAnchorPrice } from '@daedalus/core/src/offer/business/price';
import { getRoundingStrategy } from '@daedalus/core/src/utils/number';
import { HotelCardCondensed } from '@daedalus/shared/src/search/HotelCardCondensed';
import { ReviewSentiments } from '@daedalus/shared/src/search/ReviewSentiments';
import { ContentHotel, TranslatedLOVItem } from '@findhotel/sapi/dist/types/packages/core/src/types/types';
interface Props {
  hotelId: Hotel['objectID'];
  onCloseClick?: (event: React.MouseEvent) => void;
  isPopper?: boolean;
}
interface PriceProps {
  hasTopOffer: boolean;
  topOffer: SearchOffer;
  anchorPrice: number;
  anchorPriceTotal: number;
  hotelId: Hotel['objectID'];
  isAnchor: boolean;
  size?: PriceSize;
}
interface SplitBookingPriceProps {
  hotelId: Hotel['objectID'];
  anchorPrice: number;
  anchorPriceTotal: number;
  size?: PriceSize;
}
const _exp = /*#__PURE__*/() => Flex.Row;
const UnavailabilityHeaderElement = /*#__PURE__*/styled(_exp())({
  name: "UnavailabilityHeaderElement",
  class: "u1m5u5mg",
  propsAsIs: true
});
const Wrapper = /*#__PURE__*/styled('div')({
  name: "Wrapper",
  class: "w1docm9a",
  propsAsIs: false
});
const _exp7 = /*#__PURE__*/() => Flex.Row;
const IconWrapper = /*#__PURE__*/styled(_exp7())({
  name: "IconWrapper",
  class: "i1mz24nz",
  propsAsIs: true
});
const SplitBookingPrice: React.FC<SplitBookingPriceProps> = ({
  hotelId,
  anchorPrice,
  anchorPriceTotal,
  size = 'xl'
}) => {
  const currencyCode = useSelector(getCurrencyCode);
  const showTotalPrices = useSelector(getShowTotalPrices);
  const sbPriceSummary = useSelector(state => getSplitBookingPriceSummary(state, hotelId));
  const price = showTotalPrices ? sbPriceSummary.sum : sbPriceSummary.average;
  return <PriceComposition anchorPrice={showTotalPrices ? anchorPriceTotal : anchorPrice} currencyCode={currencyCode} price={price} showAnchorPrice={hasAnchorPrice(sbPriceSummary.average, anchorPrice)} size={size} showIndication showTotal={showTotalPrices} roundingStrategy={getRoundingStrategy()} shouldDisplayInline />;
};
const Price: React.FC<PriceProps> = ({
  hasTopOffer,
  topOffer,
  anchorPrice,
  anchorPriceTotal,
  hotelId,
  isAnchor,
  size = 'xl'
}) => {
  const anchorComplete = useSelector(getAnchorComplete);
  const {
    hasSplitBookingOffer,
    splitBookingPosition
  } = useSelector(state => getSplitBookingDetails(state, hotelId));
  const isOffersUnavailable = anchorComplete && !hasTopOffer && isAnchor;

  // If the top offer is a split booking, show split booking price
  if (hasSplitBookingOffer && splitBookingPosition === 0) {
    return <SplitBookingPrice anchorPrice={anchorPrice} anchorPriceTotal={anchorPriceTotal} hotelId={hotelId} />;
  }
  if (hasTopOffer) return <OfferPrice showPriceIndication anchorPrice={anchorPrice} anchorPriceTotal={anchorPriceTotal} offer={topOffer} shouldDisplayInline hotelId={hotelId} size={size} />;
  if (isOffersUnavailable) {
    return <UnavailabilityHeaderElement alignItems="center">
        <IconWrapper>
          <Icon name="Forbid" />
        </IconWrapper>
        <FormattedMessageWrapper id="unavailability.soldout" defaultMessage="Sold out on your dates" />
      </UnavailabilityHeaderElement>;
  }
};
const allFilterKeys = getAllFilterKeys();
export const HotelSummaryCardWithStore = ({
  hotelId,
  onCloseClick,
  isPopper
}: Props) => {
  const {
    isMobile
  } = useDeviceLayout();
  const hotel = useSelector(state => getHotel(state, hotelId)) as ContentHotel | Hotel;
  const topOffer = useSelector((state: State) => getHotelTopOffer(state, hotelId));
  const offerEntity = useSelector((state: State) => getOfferEntity(state, hotelId));
  const searchIsPending = useSelector(getIsSearchPending);
  const anchorHotelId = useSelector(getAnchorHotelId);
  const {
    getFilters,
    getAppliedStarsLabel
  } = useFiltersForm(allFilterKeys);
  const listOfValues = useSelector(getListOfValues);
  const currencyCode = useSelector(getCurrencyCode);
  const {
    formatMessage
  } = useIntl();
  const hasTopOffer = Boolean(topOffer);
  const anchorPrice = getAnchorPrice(offerEntity?.topOfferData);
  const anchorPriceTotal = getAnchorPriceTotal(offerEntity);
  const sentiments = (hotel?.sentiments || []).slice(0, 3);
  const isAnchor = anchorHotelId === hotelId;
  const showCard = hotel && (hasTopOffer || !searchIsPending && isAnchor);
  const filtersMatchSlot = useMemo(() => {
    if (!hotel || isAnchor) return false;
    const filters = getFilters();
    const starRatingLabel = getAppliedStarsLabel(filters?.starRatings);
    const enabledCurrencies = getEnabledCurrencies(Settings.get('ENABLED_CURRENCIES'));
    const currency = enabledCurrencies.find(currency => currency.code === currencyCode);
    const currencySymbol = prop('symbol', currency);
    const {
      unmatchedFilters
    } = getMatchingFilters(filters, hotel as Hotel, listOfValues, formatMessage, starRatingLabel, currencySymbol);
    const hasUnmatchedFilters = unmatchedFilters.length > 0;
    return hasUnmatchedFilters && <UnmatchedFilters filters={unmatchedFilters} />;
  }, [currencyCode, formatMessage, getAppliedStarsLabel, getFilters, hotel, isAnchor, listOfValues]);
  const isLoading = hotel && !hasTopOffer && !isAnchor;
  const sentimentList = useMemo(() => {
    const mapSentimentToLOV = sentiment => pathOr<string>(String(sentiment), [`Sentiment:${sentiment}`, 'value'], listOfValues);
    return sentiments?.map((sentiment): TranslatedLOVItem => ({
      id: sentiment.id ?? sentiment,
      value: mapSentimentToLOV(sentiment)
    }));
  }, [listOfValues, sentiments]);
  if (!showCard) {
    return null;
  }
  return <Wrapper>
      <HotelCardCondensed imageProviderInstance={imageProviderInstance} placeHolderImage={placeHolderImage} isPopper={isPopper} hotel={hotel} priceSlot={<Price {...{
      hasTopOffer,
      anchorPrice,
      anchorPriceTotal,
      topOffer,
      hotelId,
      isAnchor,
      size: 'lg'
    }} />} informationSlot={filtersMatchSlot || <ReviewSentiments sentiments={sentimentList} isCompact />} topRightSlot={!isMobile && <NoStyleButton onClick={onCloseClick}>
              <Icon name="Close" size="sm" />
            </NoStyleButton>} isLoading={isLoading} />
    </Wrapper>;
};

require("./HotelSummaryCardWithStore.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./HotelSummaryCardWithStore.tsx");